/**
 * Display helpers for `/app/settings/billing` (MAR-1256). Formatting and filtering only: every amount
 * shown on the page comes from `services/billing`, which does all pricing and IVA math.
 */
import type { BillingChargeRow, BillingPlanUsageRow, BillingTaxDocumentRow } from '#shared/billing/billingSettings'

/** A billing date in the UI locale (`21 nov 2026` / `Nov 21, 2026`), in UTC, with no ISO suffix. */
export function formatBillingDate(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const parts = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'es-SV', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(date)
  // es-SV renders "21 nov. 2026" or "21 de nov. de 2026" depending on the ICU build: keep day, month, year.
  if (locale !== 'en') {
    const pick = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? ''
    return `${pick('day')} ${pick('month').replace(/\.$/, '')} ${pick('year')}`
  }
  return parts.map(p => p.value).join('')
}

/**
 * The plans that have licences, active or deactivated (MAR-1310). A plan with none belongs to the rate
 * catalog only, so the statement is not a table of zeros.
 */
export function activePlanRows(plans: BillingPlanUsageRow[]): BillingPlanUsageRow[] {
  return plans.filter(plan => plan.active_seats > 0 || plan.deactivated_seats > 0)
}

/** Cents as a USD amount (`$59.99`). */
export function formatUsdFromCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

/** An amount with its sign: `-$10.00` for a negative one. */
export function formatSignedUsdFromCents(cents: number): string {
  return cents < 0 ? `-${formatUsdFromCents(-cents)}` : formatUsdFromCents(cents)
}

/** Active licences across every plan (the "N licencias" of the summary row); billing counted them. */
export function totalActiveLicences(plans: BillingPlanUsageRow[]): number {
  return plans.reduce((sum, plan) => sum + plan.active_seats, 0)
}

/** The plans holding active licences, most licences first (ties by label), for the "Plan actual" cell. */
export function activeLicencePlans(plans: BillingPlanUsageRow[]): { label: string, count: number }[] {
  return plans
    .filter(plan => plan.active_seats > 0)
    .map(plan => ({ label: plan.label, count: plan.active_seats }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

export type InvoiceStatus = 'paid' | 'overdue' | 'processing' | 'credit_note'

/** One line of the invoices table: a charge (or the attempts at one cycle's charge) or a credit note. */
export interface InvoiceRow {
  key: string
  kind: 'charge' | 'credit_note'
  /** ISO instant the row is dated by. */
  date: string
  /** Licences the charge paid for; null for a credit note and for charges recorded before the count existed. */
  members: number | null
  /** Signed: a credit note gives money back. */
  totalCents: number
  status: InvoiceStatus
  document: BillingTaxDocumentRow | null
}

const STATUS_RANK: Record<string, number> = { succeeded: 3, pending: 2, failed: 1 }

function invoiceStatusOf(status: string): InvoiceStatus {
  if (status === 'succeeded') return 'paid'
  if (status === 'failed') return 'overdue'
  return 'processing'
}

/**
 * The invoices of the organization, newest first, from billing's charges and tax documents. What counts
 * as an invoice: a paid checkout, a renewal (with its dunning retries folded into the one invoice of that
 * cycle, which is paid once any attempt succeeded and overdue while every one failed) and a prorated
 * licence that is paid or in process; an unpaid checkout or a declined proration is not one. Credit notes
 * (documents no charge points at) are listed too, negative. Pure formatting and filtering: no amount is
 * computed here.
 */
export function buildInvoiceRows(charges: BillingChargeRow[], taxDocuments: BillingTaxDocumentRow[]): InvoiceRow[] {
  const docsById = new Map(taxDocuments.map(doc => [doc.id, doc]))
  const groups = new Map<string, BillingChargeRow[]>()
  for (const charge of charges) {
    if (charge.kind === 'checkout' && charge.status !== 'succeeded') continue
    if (charge.kind === 'proration' && charge.status === 'failed') continue
    const groupKey = (charge.kind === 'renewal' || charge.kind === 'dunning') && charge.period_start
      ? `cycle:${charge.period_start}`
      : charge.id
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), charge])
  }

  const rows: InvoiceRow[] = []
  for (const [groupKey, attempts] of groups) {
    const best = Math.max(...attempts.map(a => STATUS_RANK[a.status] ?? 2))
    // The decisive attempt: the newest one at the best status (the paid retry, else the latest try).
    const decisive = attempts
      .filter(a => (STATUS_RANK[a.status] ?? 2) === best)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0]!
    const documentId = decisive.tax_document_id ?? attempts.find(a => a.tax_document_id)?.tax_document_id ?? null
    rows.push({
      key: groupKey.startsWith('cycle:') ? decisive.id : groupKey,
      kind: 'charge',
      date: decisive.kind === 'proration' ? decisive.created_at : (decisive.period_start ?? decisive.created_at),
      members: decisive.quantity,
      totalCents: decisive.total,
      status: invoiceStatusOf(decisive.status),
      document: documentId ? (docsById.get(documentId) ?? null) : null,
    })
  }

  const linked = new Set(charges.map(charge => charge.tax_document_id).filter(Boolean))
  for (const doc of taxDocuments) {
    if (linked.has(doc.id)) continue
    rows.push({
      key: doc.id,
      kind: 'credit_note',
      date: doc.issued_at ?? doc.created_at,
      members: null,
      totalCents: doc.document_type === '05' ? -doc.total_cents : doc.total_cents,
      status: 'credit_note',
      document: doc,
    })
  }
  return rows.sort((a, b) => b.date.localeCompare(a.date))
}

/** The issued document as a downloadable JSON `data:` URL (the DTE data billing holds). */
export function dteDataHref(doc: BillingTaxDocumentRow): string {
  const payload = {
    id: doc.id,
    document_type: doc.document_type,
    status: doc.status,
    issuer_nit: doc.issuer_nit,
    issuer_name: doc.issuer_name,
    codigo_generacion: doc.codigo_generacion,
    sello_recepcion: doc.sello_recepcion,
    net_cents: doc.net_cents,
    iva_cents: doc.iva_cents,
    total_cents: doc.total_cents,
    issued_at: doc.issued_at,
  }
  return `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(payload, null, 2))}`
}

export function dteFileName(doc: BillingTaxDocumentRow): string {
  return `dte-${doc.codigo_generacion ?? doc.id}.json`
}
